/**
 * Czyste funkcje na dźwięku (bez przeglądarki): zamiana nagrania z mikrofonu na 16 kHz / 16 bit mono, jakiego oczekuje Azure,
 * oraz przycięcie ciszy. Służą do tego, żeby dało się POWTÓRZYĆ rozpoznawanie na już nagranej wypowiedzi, gdy połączenie z Azure
 * się zawiesi — dziecko nie musi wtedy mówić drugi raz.
 */

export const PCM_RATE = 16000;

/**
 * Skleja kawałki (liczby zmiennoprzecinkowe −1…1 z Web Audio, zwykle 44,1 albo 48 kHz) i zamienia na 16 kHz Int16.
 * Przy zmniejszaniu częstotliwości uśrednia próbki z danego przedziału (prosty filtr, bez zniekształceń „aliasingu”).
 */
export function toPcm16k(chunks: readonly Float32Array[], rate: number): Int16Array {
  const total = chunks.reduce((n, c) => n + c.length, 0);
  const src = new Float32Array(total);
  let o = 0;
  for (const c of chunks) {
    src.set(c, o);
    o += c.length;
  }
  const clamp = (v: number): number => Math.max(-32768, Math.min(32767, Math.round(v * 32767)));
  if (rate <= 0 || total === 0) return new Int16Array(0);
  if (rate === PCM_RATE) return Int16Array.from(src, clamp);
  const ratio = rate / PCM_RATE;
  const n = Math.floor(total / ratio);
  const out = new Int16Array(n);
  for (let i = 0; i < n; i++) {
    const start = i * ratio;
    const end = start + ratio;
    if (ratio <= 1) {
      // powiększanie (rzadkie): interpolacja liniowa
      const j = Math.floor(start);
      const frac = start - j;
      out[i] = clamp((src[j] ?? 0) * (1 - frac) + (src[Math.min(j + 1, total - 1)] ?? 0) * frac);
      continue;
    }
    let sum = 0;
    let weight = 0;
    for (let j = Math.floor(start); j < Math.ceil(end) && j < total; j++) {
      const w = Math.min(j + 1, end) - Math.max(j, start); // ułamkowa waga próbki na brzegach przedziału
      sum += (src[j] ?? 0) * w;
      weight += w;
    }
    out[i] = clamp(weight > 0 ? sum / weight : 0);
  }
  return out;
}

const FRAME = PCM_RATE / 100; // 10 ms

function frameRms(pcm: Int16Array, start: number): number {
  const end = Math.min(pcm.length, start + FRAME);
  let sum = 0;
  for (let i = start; i < end; i++) {
    const v = pcm[i] ?? 0;
    sum += v * v;
  }
  return end > start ? Math.sqrt(sum / (end - start)) : 0;
}

/**
 * Ile ciszy zostaje przed pierwszą mową i po ostatniej w nagraniu do powtórki, a więc i w nagraniu wysyłanym do strojenia (SPEC §23.40). Plik zaczyna się więc `max(0, onset − REPLAY_LEAD_MS)`
 * po otwarciu mikrofonu, a czasy słów z Azure liczą się od otwarcia mikrofonu — kalibracja (scripts/lib/calibrationReport.ts) przesuwa je o tę wartość.
 */
export const REPLAY_LEAD_MS = 400;
export const REPLAY_TAIL_MS = 800;

/**
 * Zostawia mowę z zapasem ciszy po obu stronach (domyślnie REPLAY_LEAD_MS przed i REPLAY_TAIL_MS po) — resztę, w tym długi „ogon” ciszy z czasu oczekiwania na
 * zawieszone połączenie, odcina. Próg jest względny (kilka razy ponad szum tła i ułamek głośności szczytowej), więc działa przy różnych mikrofonach.
 * Brak mowy → pusta tablica.
 */
export function trimForReplay(pcm: Int16Array, leadMs = REPLAY_LEAD_MS, tailMs = REPLAY_TAIL_MS): Int16Array {
  const span = speechSpan(pcm);
  if (!span) return pcm.slice(0, 0);
  const frames = Math.ceil(pcm.length / FRAME);
  const from = Math.max(0, span.first - Math.round(leadMs / 10));
  const to = Math.min(frames, span.last + 1 + Math.round(tailMs / 10));
  return pcm.slice(from * FRAME, Math.min(pcm.length, to * FRAME));
}

/** Pierwsza i ostatnia 10-milisekundowa ramka z mową (próg względny: kilka razy ponad szum tła i ułamek głośności szczytowej) albo null. */
function speechSpan(pcm: Int16Array): { first: number; last: number } | null {
  const frames = Math.ceil(pcm.length / FRAME);
  if (frames === 0) return null;
  const rms: number[] = [];
  for (let f = 0; f < frames; f++) rms.push(frameRms(pcm, f * FRAME));
  const sorted = [...rms].sort((a, b) => a - b);
  const noise = sorted[Math.floor(sorted.length / 10)] ?? 0; // tło = cichsze 10% ramek (mowa może zajmować większość krótkiego nagrania)
  const peak = sorted[sorted.length - 1] ?? 0;
  if (peak < 40) return null; // cyfrowa cisza albo ledwo słyszalny szum
  const thr = Math.min(Math.max(25, noise * 3, peak * 0.06), peak * 0.5);
  const first = rms.findIndex((r) => r >= thr);
  if (first < 0) return null;
  let last = rms.length - 1;
  while (last > first && (rms[last] ?? 0) < thr) last--;
  return { first, last };
}

/**
 * Po ilu ms od początku nagrania zaczyna się mowa (null = nie ma mowy). Nagranie zaczyna się w chwili otwarcia mikrofonu, więc wartość bliska zeru
 * oznacza, że dziecko już mówiło, gdy mikrofon się włączył — pierwsze słowo mogło zostać ucięte.
 */
export function speechOnsetMs(pcm: Int16Array): number | null {
  const span = speechSpan(pcm);
  return span ? span.first * 10 : null;
}
